import { COORDINATE_PRECISION, roundCoordinate, roundPoint } from './coordinate.js';
import { isFolded } from './folds.js';
import { CURVE_KINDS, CURVE_SIDES, } from './types/project.js';
/**
 * 曲面(`VideoFrame.curve`)の扱い。
 *
 * 正本は [ADR-0060](../../../docs/adr/0060-curved-surface.md) の決定 6・7、
 * 持ち方は [#28 の DESIGN.md](../../../docs/issues/28-data-design/DESIGN.md)。
 *
 * **ここに置くのは型の上の規則だけ。** 形の計算(写像・短冊・表裏・当てはめ・当たり判定・輪郭)は
 * **描画コア**に置く(ADR-0060 の決定 7)。
 *
 * **S04 と業務 API が同じ関数を使う。** S04 は当てはめの結果を保存の前にこれで確かめる ——
 * UI が通した値を API が弾くと**自動保存が止まり、ほかの編集まで保存されない**
 * ([ADR-0035](../../../docs/adr/0035-autosave-by-polling.md))。
 */
/**
 * **曲面の枠か。** `curve` がある。
 *
 * **判定はこの関数だけで行う**([`isFolded`](./folds.ts) と同じ考え)。
 * **型ガードにしている** —— 真なら `curve` が在ると分かるので、呼び出し側で `null` の判定を重ねずに済む。
 *
 * **社内の枠(`VideoFrame`)にも公開の枠(`PublicVideoFrame`)にも使える。** 受ける形を `PublicCurve`
 * (`kind` と `shape` だけ)にし、真の側は**渡した枠の `curve` の型**に絞る —— ビューアが別に `curve != null` と書かずに済む。
 */
export function isCurved(frame) {
    return frame.curve != null;
}
/**
 * **正面の位置(`front`)を持つ形か。** 左右とも柱の輪郭 —— `column`、または `sides` が両方 `contour` の `convex`
 * (ADR-0060 の決定 2。裏に回る範囲があるので、どこを写真の正面に向けるかを決める必要がある)。
 */
export function curveHasFront(curve) {
    if (curve.kind === 'column')
        return true;
    return curve.kind === 'convex' && curve.sides?.[0] === 'contour' && curve.sides[1] === 'contour';
}
/** 1 周(ラジアン)。 */
const FULL_TURN = 2 * Math.PI;
/**
 * **角度の幅に許す丸めの誤差。** 両端を別々に小数第 6 位へ丸めるので、幅は最大 10⁻⁶ 動く。
 * 柱の一周の `±π` は `±3.141593` に丸まり、幅 `6.283186` は **2π を 6.9×10⁻⁷ 超える** —— 許さないと、
 * 保存した値を送り直しただけで 400 になる。
 */
const ROUNDING_SLACK = 10 ** -COORDINATE_PRECISION;
/**
 * **保存する値**(小数第 6 位に丸めた値)。有限数に丸まらなければ `NaN`。
 *
 * **規則はこの値で判定する。** 丸めた後に規則を外れる値(0 に丸まる正の数・0 に丸まる向き・
 * 丸めで 2π を超える幅・丸めで無限大にあふれる数)を通すと、**保存した値を送り直したときに弾かれる**
 * —— 判定を保存の形に対して閉じておく。
 */
function stored(value) {
    if (!Number.isFinite(value))
        return Number.NaN;
    const rounded = roundCoordinate(value);
    return Number.isFinite(rounded) ? rounded : Number.NaN;
}
function isStorable(value) {
    return !Number.isNaN(stored(value));
}
/**
 * **構造として受け付けられない理由の一覧。** 空なら受け付けられる(ADR-0060 の決定 6 の表)。
 *
 * - **`folds` と同時には持てない**(同意書の仮定 F)。境目が空の `folds` は保存時に落ちるので併用とみなさない
 * - 種類・左右の辺の値が値の集合に入る
 * - **`sides` は `convex` だけが持ち、必須**
 * - **`front` は左右とも輪郭のとき([`curveHasFront`](#curveHasFront))だけ持ち、必須。** `0` 以上 `1` 未満
 * - `rims` と `shape` の数が有限、`aspect`・`focal`・`height` が正、`axis`・`ref` が 0 でない、
 *   **角度の幅が 0 より大きく 2π 以下**(丸めの誤差ぶんは許す)
 *
 * **数の規則は保存する値(丸めた値)で判定する**(上の `stored`)。`front` は丸めないのでそのまま見る。
 *
 * **`rims` の範囲は見ない** —— `quad` と同じくキャンバスの外を許容する。
 * **形の正しさ(写真と合っているか・軸と `ref` が平行でないか)は見ない** —— 描画コアがその形を描けなければ描かないだけ。
 */
export function curveProblems(curve, folds) {
    if (curve == null)
        return [];
    const problems = [];
    const add = (path, message) => {
        problems.push({ path: ['curve', ...path], message });
    };
    if (isFolded({ folds })) {
        add([], '折れた枠は曲面にできません。折れ目を外してください');
    }
    if (!CURVE_KINDS.includes(curve.kind)) {
        add(['kind'], '曲面の種類は column / convex / concave のどれかにしてください');
    }
    else {
        sideAndFrontProblems(curve, add);
    }
    curve.rims.forEach((point, index) => {
        if (!point.every(isStorable))
            add(['rims', index], '座標は有限数にしてください');
    });
    shapeProblems(curve.shape, (path, message) => {
        add(['shape', ...path], message);
    });
    return problems;
}
/** 種類と `sides` / `front` の対応。**種類が値の集合に入っているときだけ**呼ぶ。 */
function sideAndFrontProblems(curve, add) {
    if (curve.kind === 'convex') {
        if (curve.sides == null) {
            add(['sides'], '外向きの曲面は左右の辺の種類が必要です');
            // **`front` は見ない** —— 要るかどうかが辺の種類で決まる(意味の通らない指摘を並べない)。
            return;
        }
        const unknown = curve.sides.flatMap((side, index) => CURVE_SIDES.includes(side) ? [] : [index]);
        for (const index of unknown) {
            add(['sides', index], '辺の種類は end / contour のどちらかにしてください');
        }
        if (unknown.length > 0)
            return;
    }
    else if (curve.sides != null) {
        add(['sides'], '左右の辺の種類は外向きの曲面だけが持ちます');
    }
    if (!curveHasFront(curve)) {
        if (curve.front != null)
            add(['front'], '正面の位置は左右とも柱の輪郭のときだけ持ちます');
        return;
    }
    if (curve.front == null) {
        add(['front'], '左右とも柱の輪郭のときは正面の位置が必要です');
    }
    else if (!Number.isFinite(curve.front) || curve.front < 0 || curve.front >= 1) {
        add(['front'], '正面の位置は 0 以上 1 未満にしてください');
    }
}
/** `shape` の数の規則。`path` は `shape` からの位置。 */
function shapeProblems(shape, add) {
    for (const key of ['aspect', 'focal', 'height']) {
        const value = stored(shape[key]);
        if (Number.isNaN(value))
            add([key], '有限数にしてください');
        else if (value <= 0)
            add([key], '0 より大きい数にしてください');
    }
    for (const key of ['center', 'axis', 'ref']) {
        const values = shape[key].map(stored);
        if (values.some(Number.isNaN))
            add([key], '有限数にしてください');
        else if (key !== 'center' && values.every((value) => value === 0)) {
            add([key], '0 でない向きにしてください');
        }
    }
    const left = stored(shape.angles[0]);
    const right = stored(shape.angles[1]);
    if (Number.isNaN(left) || Number.isNaN(right)) {
        add(['angles'], '有限数にしてください');
        return;
    }
    const span = Math.abs(right - left);
    if (span <= 0 || span > FULL_TURN + ROUNDING_SLACK) {
        add(['angles'], '角度の幅は 0 より大きく 2π 以下にしてください');
    }
}
/**
 * 保存する形にそろえる。**`rims` と `shape` を小数第 6 位で丸め、項目を選んで作り直す。**
 * `null` / 省略は `undefined`(「無し」の表現は 1 つ —— 保存時に項目ごと落とす)。
 *
 * **`front` は丸めない**(ADR-0060 の決定 6 で丸めるのは `rims` と `shape`。丸めると `0.9999996` が `1` になり範囲を外れる)。
 * **[`curveProblems`](#curveProblems) を通った値を渡す** —— 有限数でない値は丸められない(`roundCoordinate` が投げる)。
 */
export function normalizeCurve(curve) {
    if (curve == null)
        return undefined;
    const { shape } = curve;
    return {
        kind: curve.kind,
        rims: [roundPoint(curve.rims[0]), roundPoint(curve.rims[1])],
        ...(curve.sides == null ? {} : { sides: [curve.sides[0], curve.sides[1]] }),
        ...(curve.front == null ? {} : { front: curve.front }),
        shape: {
            aspect: roundCoordinate(shape.aspect),
            focal: roundCoordinate(shape.focal),
            center: roundVector(shape.center),
            axis: roundVector(shape.axis),
            ref: roundVector(shape.ref),
            height: roundCoordinate(shape.height),
            angles: [roundCoordinate(shape.angles[0]), roundCoordinate(shape.angles[1])],
        },
    };
}
function roundVector(vector) {
    return [roundCoordinate(vector[0]), roundCoordinate(vector[1]), roundCoordinate(vector[2])];
}
//# sourceMappingURL=curve.js.map